import { ColDef, ColumnMenuTab, GridApi, StatusPanelDef } from 'ag-grid-community';
import { deburr } from 'lodash';
import { defaultDateFormat, defaultDateTimeFormat, defaultFullDateTimeFormat } from './moment';
import { Icon } from 'tacho-ui';
import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { TachoColumnState } from '../domainModels/displayPreferences/tachoColumnState';
import { ColDef as AllModulesColDef, Column } from 'ag-grid-community';

export const fieldName = <T>(name: keyof T) => name;

export const assignColDefsDefaults = (
    colDefs: Array<ColDef> | Array<AllModulesColDef>,
    defaults: Array<TachoColumnState>,
    assignHide = true,
) => {
    if (!defaults) {
        return;
    }

    colDefs.forEach((colDef, index) => {
        const defColumnIndex = defaults.findIndex(c => c.colId === colDef.colId);
        (colDef as any).order = defColumnIndex !== -1 ? defColumnIndex : index;
        if (defColumnIndex === -1) {
            return;
        }
        const defColumn = defaults[defColumnIndex];
        colDef.initialSort = defColumn.sort !== undefined ? defColumn.sort : colDef.sort;
        if (defColumn.sortIndex !== undefined && defColumn.sortIndex !== null) {
            (colDef as ColDef & { sortIndex?: number | null }).sortIndex = defColumn.sortIndex;
        }
        if (assignHide) {
            colDef.hide = defColumn.hide !== undefined ? defColumn.hide : colDef.hide;
        }
        colDef.width = defColumn.width !== undefined ? defColumn.width : colDef.width;
    });
    colDefs.sort((c1, c2) => (c1 as any).order - (c2 as any).order);
};

export const filterMenuTab: Array<ColumnMenuTab> = ['filterMenuTab'];

export const COLUMN_WIDTH = {
    XXS: 50,
    XS: 65,
    SM: 105,
    MD: 150,
    LG: 180,
    XL: 250,
    XXL: 330,
} as const;

export const compareFullDateTimeFilter = (valueA: string, valueB: string) => {
    const dateA = DateTime.fromFormat(valueA, defaultFullDateTimeFormat);
    const dateB = DateTime.fromFormat(valueB, defaultFullDateTimeFormat);
    if (!valueA) {
        return 1;
    }
    if (!valueB) {
        return 1;
    }
    return dateA.hasSame(dateB, 'second') ? 0 : dateA > dateB ? -1 : 1;
};

export const compareDateTimeFilter = (valueA: string, valueB: string) => {
    const dateA = DateTime.fromFormat(valueA, defaultDateTimeFormat);
    const dateB = DateTime.fromFormat(valueB, defaultDateTimeFormat);
    if (!valueA) {
        return 1;
    }
    if (!valueB) {
        return 1;
    }
    return dateA.hasSame(dateB, 'minute') ? 0 : dateA > dateB ? -1 : 1;
};

export const compareDateFilter = (valueA: string, valueB: string) => {
    const dateA = DateTime.fromFormat(valueA, defaultDateFormat);
    const dateB = DateTime.fromFormat(valueB, defaultDateFormat);

    if (!valueA) {
        return 1;
    }
    if (!valueB) {
        return 1;
    }
    return dateA.hasSame(dateB, 'day') ? 0 : dateA > dateB ? -1 : 1;
};

export const compareDate = (
    valueA: string | number,
    valueB: string | number,
    dateA: DateTime,
    dateB: DateTime,
    isInverted: boolean,
) => {
    if (valueA === valueB && !isInverted) {
        return dateA?.hasSame(dateB, 'day') ? 0 : dateA > dateB ? -1 : 1;
    } else if (valueA === valueB) {
        return dateA?.hasSame(dateB, 'day') ? 0 : dateA > dateB ? 1 : -1;
    }
    return valueA > valueB ? 1 : -1;
};

export const compareDateInverted = (
    valueA: string | number,
    valueB: string | number,
    dateA: DateTime,
    dateB: DateTime,
    isInverted: boolean,
) => {
    if (valueA === valueB && !isInverted) {
        return dateA?.hasSame(dateB, 'day') ? 0 : dateA > dateB ? 1 : -1;
    } else if (valueA === valueB) {
        return dateA?.hasSame(dateB, 'day') ? 0 : dateA > dateB ? -1 : 1;
    }
    return valueA > valueB ? -1 : 1;
};

export const autoFitLastColumn = (allColumns: Array<Column> | null, api: GridApi) => {
    const columns = allColumns || [];
    if (columns.length === 0) return;

    const columnState = columns.map((col, index) => ({
        colId: col.getColId(),
        flex: index === columns.length - 1 ? 1 : undefined,
    }));

    api.applyColumnState({
        state: columnState,
        applyOrder: false, // Keep existing order
    });
};

export const compareFilterList = (a: string, b: string) => {
    if (a === '(puste)') {
        return -1;
    }
    if (b === '(puste)') {
        return 1;
    }
    const regex = /[^A-Za-z0-9]/g;
    const valA = typeof a === 'string' ? deburr(a?.toLowerCase())?.replace(regex, '') : '';
    const valB = typeof b === 'string' ? deburr(b?.toLowerCase())?.replace(regex, '') : '';
    if (valA === valB) return 0;
    return valA.localeCompare(valB);
};

export const compareFilterListWithNumbers = (a: string, b: string) => {
    const valA = +a;
    const valB = +b;
    if (valA === valB) return 0;
    return valA > valB ? 1 : -1;
};

export const getDefaultGridContextMenuItems = (
    formatMessage: ({ id }: { id: string }) => string,
    gridApi: GridApi | undefined,
) => {
    const options = [
        {
            id: '0',
            label: formatMessage({ id: 'gridMenuResetfilters' }),
            onItemClick: () => {
                gridApi?.setFilterModel(null);
            },
            iconName: Icon.Restore,
        },
    ];
    return options;
};

export const useStatusBar = () => {
    const statusBar = useMemo<{
        statusPanels: Array<StatusPanelDef>;
    }>(() => {
        return {
            statusPanels: [{ statusPanel: 'agSelectedRowCountComponent', align: 'right' }],
        };
    }, []);
    return statusBar;
};

export const dateSortComparator = (valueA: unknown, valueB: unknown): number => {
    const toMs = (v: unknown): number => {
        if (v == null) return 0;
        if (typeof v === 'object' && v !== null && 'toMillis' in v) return (v as DateTime).toMillis();
        if (typeof v === 'string' && v) {
            const dt = /^\d{4}-\d{2}-\d{2}$/.test(v) ? DateTime.fromISO(v) : DateTime.fromFormat(v, defaultDateFormat);
            return dt.isValid ? dt.toMillis() : 0;
        }
        return 0;
    };
    return toMs(valueA) - toMs(valueB);
};
